import { FracasIncident } from '../../types/fracas.types';

export interface FracasMetrics {
    totalIncidents: number;
    openIncidents: number;
    criticalIncidents: number;
    averageTimeToClose: number;
    incidentsByStatus: Record<string, number>;
    incidentsBySeverity: Record<string, number>;
    topFailureModes: Array<{ mode: string; count: number }>;
    monthlyTrend: Array<{ month: string; count: number }>;
}

export const calculateFracasMetrics = (incidents: FracasIncident[]): FracasMetrics => {
    const totalIncidents = incidents.length;
    const openIncidents = incidents.filter(i => i.status === 'Open').length;
    const criticalIncidents = incidents.filter(i => i.severity === 'Critical').length;

    // Calculate average time to close (in days)
    const closedIncidents = incidents.filter(i => i.status === 'Closed');
    const averageTimeToClose = closedIncidents.length > 0
        ? Math.round(closedIncidents.reduce((sum, incident) => {
            const reported = new Date(incident.reportedAt).getTime();
            const closed = incident.closedAt ? new Date(incident.closedAt).getTime() : Date.now();
            const days = (closed - reported) / (1000 * 60 * 60 * 24);
            return sum + days;
        }, 0) / closedIncidents.length)
        : 0;

    // Grouping
    const incidentsByStatus: Record<string, number> = {};
    const incidentsBySeverity: Record<string, number> = {};
    const failureModeMap: Record<string, number> = {};

    incidents.forEach(incident => {
        incidentsByStatus[incident.status] = (incidentsByStatus[incident.status] || 0) + 1;
        incidentsBySeverity[incident.severity] = (incidentsBySeverity[incident.severity] || 0) + 1;
        if (incident.failureMode) {
            failureModeMap[incident.failureMode] = (failureModeMap[incident.failureMode] || 0) + 1;
        }
    });

    const topFailureModes = Object.entries(failureModeMap)
        .map(([mode, count]) => ({ mode, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

    // Monthly trend (last 12 months)
    const monthlyTrend: Array<{ month: string; count: number }> = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const month = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        const count = incidents.filter(incident => {
            const incidentDate = new Date(incident.reportedAt);
            return incidentDate.getMonth() === d.getMonth() &&
                incidentDate.getFullYear() === d.getFullYear();
        }).length;
        monthlyTrend.push({ month, count });
    }

    return {
        totalIncidents,
        openIncidents,
        criticalIncidents,
        averageTimeToClose,
        incidentsByStatus,
        incidentsBySeverity,
        topFailureModes,
        monthlyTrend,
    };
};
